import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Loader2, User, Clock, AlertTriangle, Terminal, X } from "lucide-react";
import { format } from "date-fns";
import { useEventDetails, EventDetail } from "@/hooks/useEventDetails";
import { Card, CardContent } from "@/components/ui/card";

// Safely parse event timestamp to local time
const parseEventTime = (timeValue: string | null | undefined): Date => {
  if (!timeValue) return new Date();
  try {
    // Handle various timestamp formats from Supabase
    // Format: "2025-12-16 11:54:24.316+00" or "2025-12-16T11:54:24.316Z"
    let normalized = String(timeValue).trim();
    // Replace space with T for ISO format
    normalized = normalized.replace(' ', 'T');
    // Handle +00 timezone format (convert to Z)
    normalized = normalized.replace(/\+00$/, 'Z');
    // Ensure Z suffix for UTC
    if (!normalized.endsWith('Z') && !normalized.includes('+') && !normalized.includes('-', 10)) {
      normalized += 'Z';
    }
    const date = new Date(normalized);
    if (isNaN(date.getTime())) return new Date();
    return date;
  } catch {
    return new Date();
  }
};

interface EventDetailsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  hostname: string;
  eventType: 'login_success' | 'login_failure' | 'account_locked' | 'service_crash' | 'app_crash' | 'powershell' | null;
  eventTitle: string;
  timeRange: string;
}

export function EventDetailsDialog({
  open,
  onOpenChange,
  hostname,
  eventType,
  eventTitle,
  timeRange,
}: EventDetailsDialogProps) {
  // Only fetch when dialog is open and we have valid params
  const shouldFetch = open && !!hostname && !!eventType && !!timeRange;
  const { events, loading, error } = useEventDetails(
    shouldFetch ? hostname : '',
    shouldFetch ? eventType : null,
    shouldFetch ? timeRange : ''
  );

  const getEventIcon = (type: string) => {
    switch (type) {
      case 'login_success':
      case 'login_failure':
        return <User className="h-4 w-4" />;
      case 'account_locked':
        return <AlertTriangle className="h-4 w-4" />;
      case 'powershell':
        return <Terminal className="h-4 w-4" />;
      default:
        return <X className="h-4 w-4" />;
    }
  };

  const extractUserFromEvent = (event: EventDetail): string => {
    try {
      // Try to extract username from message
      const message = event.message || '';
      
      // For login events
      const accountNameMatch = message.match(/Account Name:\s+([^\s\n]+)/i);
      if (accountNameMatch) return accountNameMatch[1];
      
      // For account locked events
      const targetAccountMatch = message.match(/Target Account:\s+([^\s\n]+)/i);
      if (targetAccountMatch) return targetAccountMatch[1];
      
      // For service/app crashes
      const serviceMatch = message.match(/Service:\s+([^\s\n]+)/i);
      if (serviceMatch) return serviceMatch[1];
      
      // For PowerShell
      const userMatch = message.match(/User\s+=\s+([^\s\n]+)/i);
      if (userMatch) return userMatch[1];
      
      // Fallback to user_sid
      if (event.user_sid) return event.user_sid;
      
      return 'System';
    } catch {
      return 'Unknown';
    }
  };

  const extractResourceFromEvent = (event: EventDetail): string => {
    try {
      const message = event.message || '';
      
      // For service crashes
      const serviceMatch = message.match(/service name is\s+([^\s\n.]+)/i) || 
                          message.match(/The\s+([^\s]+)\s+service/i);
      if (serviceMatch) return serviceMatch[1];
      
      // For app crashes  
      const appMatch = message.match(/Application:\s+([^\s\n]+)/i) ||
                       message.match(/Faulting application name:\s+([^\s\n,]+)/i);
      if (appMatch) return appMatch[1];
      
      // For PowerShell scripts
      const scriptMatch = message.match(/Script Name\s+=\s+([^\s\n]+)/i) ||
                         message.match(/Path:\s+([^\s\n]+\.ps1)/i);
      if (scriptMatch) return scriptMatch[1];
      
      return event.provider_name || 'N/A';
    } catch {
      return 'N/A';
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {eventType && getEventIcon(eventType)}
            {eventTitle} - Details
          </DialogTitle>
          <DialogDescription>
            Showing events from the last {timeRange} for {hostname}
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="h-[60vh] pr-4">
          {loading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          )}

          {error && (
            <div className="text-center py-8 text-destructive">
              <AlertTriangle className="h-8 w-8 mx-auto mb-2" />
              <p>{error}</p>
            </div>
          )}

          {!loading && !error && events.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              <p>No events found in the selected time range</p>
            </div>
          )}

          <div className="space-y-3">
            {events.map((event) => (
              <Card key={event.id} className="border-l-4 border-l-primary/50">
                <CardContent className="pt-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* User/Resource Info */}
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        {eventType === 'service_crash' || eventType === 'app_crash' ? (
                          <>
                            <Terminal className="h-3 w-3" />
                            <span>Resource</span>
                          </>
                        ) : eventType === 'powershell' ? (
                          <>
                            <Terminal className="h-3 w-3" />
                            <span>Script</span>
                          </>
                        ) : (
                          <>
                            <User className="h-3 w-3" />
                            <span>User</span>
                          </>
                        )}
                      </div>
                      <div className="font-medium">
                        {eventType === 'service_crash' || eventType === 'app_crash' || eventType === 'powershell' 
                          ? extractResourceFromEvent(event)
                          : extractUserFromEvent(event)
                        }
                      </div>
                      {eventType === 'powershell' && (
                        <div className="text-xs text-muted-foreground">
                          User: {extractUserFromEvent(event)}
                        </div>
                      )}
                    </div>

                    {/* Time Info */}
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Clock className="h-3 w-3" />
                      <span>Time</span>
                      </div>
                      <div className="font-medium">
                        {format(parseEventTime(event.event_time), 'dd/MM/yyyy')}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {format(parseEventTime(event.event_time), 'HH:mm:ss')}
                      </div>
                    </div>

                    {/* Event ID */}
                    <div className="space-y-1">
                      <div className="text-sm text-muted-foreground">Event ID</div>
                      <div>
                        <Badge variant="outline">{event.event_id}</Badge>
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {event.provider_name}
                      </div>
                    </div>
                  </div>

                  {/* Message Preview */}
                  {event.message && (
                    <div className="mt-3 pt-3 border-t">
                      <p className="text-sm text-muted-foreground line-clamp-2">
                        {event.message}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
